import {useState} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {FiltrosDeEstado, type OpcionEstado} from '@/components/ui/filtros-de-estado'
import {CoverageRequestAction, openByEmployee} from '@/components/solicitudes-block'
import type {Evidence} from '@/hooks/sgsst/evidencias/use-evidence'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import type {Solicitud} from '@/hooks/sgsst/evidencias/use-solicitudes'
import type {SoporteCasilla} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {coveredSet, efectivaFuente, uncoveredActives} from '@/lib/sgsst-readiness'

type CoberturaFiltro = 'sin_cubrir' | 'cubiertas' | 'todas'

/** Spanish list conjunction: «A», «A y B», «A, B y C». */
function joinTitulos(casillas: SoporteCasilla[]): string {
  const titles = casillas.map((c) => c.titulo)
  if (titles.length <= 1) return titles.join('')
  return `${titles.slice(0, -1).join(', ')} y ${titles[titles.length - 1]}`
}

/** Spec §6: the action that does not lead renders disabled-with-why, never a hard block. */
function UncoveredActions({
  casilla,
  employee,
  solicitud,
  onRequest,
  onUploadFor,
}: {
  casilla: SoporteCasilla | undefined
  employee: Employee
  solicitud: Solicitud | undefined
  onRequest: () => void
  onUploadFor?: (employee: Employee, soporteKey: string | null) => void
}) {
  const {t} = useTranslation()
  if (!casilla || !onUploadFor) return <CoverageRequestAction solicitud={solicitud} onRequest={onRequest} />
  const fuente = efectivaFuente(casilla, employee.employment_type)
  if (fuente === 'empleado') {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
        <CoverageRequestAction solicitud={solicitud} onRequest={onRequest} />
        <button
          type="button"
          disabled
          title={t('evidence.coverage.subirNoAplica')}
          data-testid="coverage-upload-disabled"
          className="cursor-not-allowed text-xs whitespace-nowrap text-muted-foreground/60">
          {t('evidence.coverage.uploadFor')}
        </button>
      </span>
    )
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      <button
        type="button"
        onClick={() => onUploadFor(employee, casilla.key)}
        data-testid="coverage-upload-for"
        className="text-xs font-medium whitespace-nowrap text-primary hover:underline">
        {t('evidence.coverage.uploadFor')}
      </button>
      {casilla.medico ? (
        // The person usually keeps a copy of their own concepto (3.1.4), so the request stays live.
        <CoverageRequestAction solicitud={solicitud} onRequest={onRequest} />
      ) : (
        <button
          type="button"
          disabled
          title={t('evidence.coverage.solicitarNoAplica')}
          data-testid="coverage-request-disabled"
          className="cursor-not-allowed text-xs whitespace-nowrap text-muted-foreground/60">
          {t('solicitudes.request')}
        </button>
      )}
    </span>
  )
}

/** Closed years only report who was covered: historical rosters don't exist, so no gaps. */
export function CoverageLine({
  businessId,
  standardCode,
  items,
  roster,
  year,
  isClosed,
  workerCount,
  solicitudes,
  onRequest,
  personCasilla,
  onUploadFor,
  companyCasillas = [],
}: {
  businessId: string
  standardCode: string
  items: Evidence[]
  roster: Employee[]
  year: number
  isClosed: boolean
  workerCount: number
  solicitudes: Solicitud[]
  onRequest?: (employee: Employee) => void
  /** Soportes esperados spec §4: its fuente decides which per-person action leads. */
  personCasilla?: SoporteCasilla
  onUploadFor?: (employee: Employee, soporteKey: string | null) => void
  /** Sibling company-wide casillas (e.g. 1.1.4's planilla PILA): the table only counts `personCasilla`. */
  companyCasillas?: SoporteCasilla[]
}) {
  const {t} = useTranslation()
  // U19. Filtra en el cliente: la nómina del estándar ya llegó entera y esta lista no pagina.
  const [filtro, setFiltro] = useState<CoberturaFiltro>('sin_cubrir')

  if (isClosed) {
    return (
      <div data-testid={`coverage-line-${standardCode}`} className="space-y-1">
        {personCasilla && (
          <p
            className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase"
            data-testid="coverage-tracking-label">
            {t('evidence.coverage.trackingLabel', {titulo: personCasilla.titulo})}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {t('evidence.coverage.closedLine', {count: coveredSet(standardCode, items).size, year})}
        </p>
      </div>
    )
  }

  const actives = roster.filter((employee) => employee.status === 'active')
  if (actives.length === 0) {
    return (
      <p data-testid={`coverage-line-${standardCode}`} className="text-xs text-muted-foreground">
        {t('evidence.coverage.noActives')}{' '}
        <Link to={`/businesses/${businessId}/employees`} className="font-medium text-primary hover:underline">
          {t('evidence.coverage.goToEmployees')}
        </Link>
      </p>
    )
  }

  const uncovered = uncoveredActives(standardCode, items, roster)
  const covered = actives.length - uncovered.length
  const openRequests = openByEmployee(solicitudes)
  const uncoveredIds = new Set(uncovered.map((employee) => employee.id))
  const visibles =
    filtro === 'sin_cubrir'
      ? uncovered
      : filtro === 'cubiertas'
        ? actives.filter((employee) => !uncoveredIds.has(employee.id))
        : actives
  const opciones: OpcionEstado<CoberturaFiltro>[] = [
    {valor: 'sin_cubrir', etiqueta: t('evidence.coverage.filtro.sinCubrir'), conteo: uncovered.length},
    {valor: 'cubiertas', etiqueta: t('evidence.coverage.filtro.cubiertas'), conteo: covered},
    {valor: 'todas', etiqueta: t('evidence.coverage.filtro.todas'), conteo: actives.length},
  ]
  return (
    <div data-testid={`coverage-line-${standardCode}`} className="space-y-1.5">
      {personCasilla && (
        <p
          className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase"
          data-testid="coverage-tracking-label">
          {t('evidence.coverage.trackingLabel', {titulo: personCasilla.titulo})}
        </p>
      )}
      {companyCasillas.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="coverage-other-docs-hint">
          {t('evidence.coverage.otherDocsHint', {
            count: companyCasillas.length,
            titulos: joinTitulos(companyCasillas),
          })}
        </p>
      )}
      <div className="flex items-center gap-2.5">
        <span className="h-1.5 w-24 overflow-hidden rounded-full bg-secondary" aria-hidden="true">
          <span
            className="block h-full rounded-full bg-primary"
            style={{width: `${(covered / actives.length) * 100}%`}}
          />
        </span>
        <p className="text-xs text-foreground">
          <span className="font-mono font-medium">{covered}</span>{' '}
          {t('evidence.coverage.line', {active: actives.length})}
          {workerCount > actives.length && (
            <span className="text-muted-foreground">
              {' '}
              · {t('evidence.coverage.declaredGap', {workerCount, active: actives.length})}
            </span>
          )}
        </p>
      </div>
      {uncovered.length > 0 && (
        <div data-testid="coverage-uncovered-details">
          {/* Sin «i» propio: el criterio ya se explica en la cabecera de «Qué subir aquí». */}
          <FiltrosDeEstado
            opciones={opciones}
            valor={filtro}
            onValor={setFiltro}
            testIdPrefijo={`coverage-filtro-${standardCode}`}
          />
          <ul className="mt-2 max-w-lg divide-y divide-border rounded-lg border border-border">
            {visibles.map((employee) => (
              <li key={employee.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3 py-2 text-xs">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/businesses/${businessId}/empleados/${employee.id}?tab=soportes`}
                    className="font-medium text-foreground hover:text-primary hover:underline"
                    data-testid="coverage-uncovered-profile-link">
                    {employee.name} {employee.last_name}
                  </Link>
                  <span className="ml-2 font-mono text-[11px] text-muted-foreground">{employee.cedula}</span>
                  {(employee.employment_type === 'contratista' || employee.employment_type === 'aprendiz') && (
                    <span className="block text-[11px] text-muted-foreground italic">
                      {t('evidence.coverage.contractorHint')}
                    </span>
                  )}
                </div>
                {onRequest && uncoveredIds.has(employee.id) && (
                  <div className="shrink-0">
                    <UncoveredActions
                      casilla={personCasilla}
                      employee={employee}
                      solicitud={openRequests.get(employee.id)}
                      onRequest={() => onRequest(employee)}
                      onUploadFor={onUploadFor}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
